(ns ycweb.app.website.cart.pages.payment.ebucks
  (:require
   [clojure.string :as str]
   [rum.core :as rum]
   [util.transform :as trf]
   [ycweb.app.website.cart.icons :as icons]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.forms :as forms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;; The raw eBucks message is kept in the payment row (PHP parity); only the
;; on-screen wording is softened for the one case that is nearly always a
;; customer typing the wrong identifier.
(defn- friendly-login-error [msg]
  (cond
    (nil? msg) "Login failed. Please try again."
    (str/starts-with? msg "Could not find")
    "We couldn't find an eBucks account for that ID or card number. Please check it and try again."
    :else msg))

;; eBucks Step 01: Login
(rum/defc ebucks-step-01-login < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [ebucks-data-atom (rum/cursor-in app-atom [:cart :ebucks-payment])
        {:keys [id-number pin]} (rum/react ebucks-data-atom)
        error (rum/cursor-in ebucks-data-atom [:error])
        order (get-in (rum/react cart-atom) [:order])
        order-number (:number order)
        order-uuid (:uuid order)

        handle-login
        (fn [e]
          (.preventDefault e)
          (model/clear-checkout-error app-atom)
          (cond
            ;; eBucks wants an ID or card number; an email is the single most
            ;; common reason logins fail, so catch it before the API call
            (and (not-empty id-number) (re-find #"@" id-number))
            (swap! ebucks-data-atom assoc :error
              "Please enter your RSA ID number or eBucks card number, not your email address.")

            (and (not-empty id-number) (not-empty pin))
            (do
              (swap! ebucks-data-atom assoc :loading true :error nil)
              (model/ebucks-login app-atom
                {:order-uuid order-uuid
                 :id-number id-number
                 :pin pin}
                (fn [response]
                  (swap! ebucks-data-atom dissoc :loading)
                  (if (:success response)
                    (do
                      (swap! ebucks-data-atom merge
                        {:session-token (:session_token response)
                         :accounts (:accounts response)
                         :payment-uuid (:payment_uuid response)
                         :otp-exempt (boolean (:otp_exempt response))})
                      (cart.common/push-substep process-atom :claim))
                    (swap! ebucks-data-atom assoc :error
                      (friendly-login-error (:msg response)))))))))]

    (cart.common/checkout-layout process-atom false
      (list
        (when-let [error-msg (rum/react error)]
          (rum/with-key (feedback/feedback :error error-msg nil) "ebucks-login-error"))
        [:div {:key "ebucks-login" :class "u-max-width-480"}
         [:div {:class "u-max-width-64 u-margin-bottom-16"}
          (icons/ebucks-icon)]
         [:h2 {:class "yc-heading yc-heading--default"} "Log in to your eBucks account"]
         (forms/input "RSA ID or eBucks Card Number:" "e.g. 8101010000000"
           (rum/cursor-in ebucks-data-atom [:id-number])
           {:type :user
            :name "conf[idnumber]"
            :id "idnumber"
            :class "js-text-input js-cta-input-trigger"})
         (forms/input "eBucks Pin:" "Enter your eBucks PIN"
           (rum/cursor-in ebucks-data-atom [:pin])
           {:type :password
            :autocomplete "off"
            :name "conf[pin]"
            :id "pin"
            :class "js-text-input js-cta-input-trigger"})
         [:p {:class "yc-paragraph"}
          [:a {:href "/help.htm?action=article&id=269&name=How-to-find-your-eBucks-pin"
               :class "text-link text-link--muted js-mfp-open-url js-prevent-default"
               :target_url "/help.htm?action=article&id=269&overrideincludes=blank"
               :data-title "How to find your eBucks pin"
               :data-content-wrapper-class "yc-simple-text"} "Forgot your eBucks PIN?"]]
         [:p {:class "yc-paragraph yc-paragraph--flush"}
          "Would you like to choose a "
          [:a.text-link
           (urlstate/href-props app-atom "order" "ShowCart"
             {:orderid (:id order) :h (:h order) :step "payment/method-postcheckout" :method "eBucks"})
           "different payment method?"]]])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Continue"
          {:on-click handle-login
           :style :primary
           :class "checkout-cta__button"
           :disabled (or (empty? id-number) (empty? pin) (cmn/curact ebucks-data-atom [:loading]))})
        nil
        order-number
        cart-atom
        {:total-key :balance}))))

;; Settle the claimed amount. On failure the substep matches the dispatcher in
;; ebucks-page: :error-otp for a wrong OTP, :error for everything else (the
;; old :error-response key was never handled and silently fell back to login).
(defn- submit-payment [app-atom process-atom ebucks-data-atom order amount otp]
  (swap! ebucks-data-atom assoc :loading true :error nil :otp-resend-feedback nil)
  (model/ebucks-payment app-atom
    {:order-uuid (:uuid order)
     :payment-uuid (:payment-uuid @ebucks-data-atom)
     :session-token (:session-token @ebucks-data-atom)
     :otp otp
     :account (:selected-account @ebucks-data-atom)
     :amount (trf/tocurrency amount)}
    (fn [response]
      (swap! ebucks-data-atom assoc :loading false)
      (if (:success response)
        (do
          (swap! ebucks-data-atom merge
            {:ebucks-ref (:ebucks_ref response)
             :settled-amount (:amount response)}) ; Amount returned is in cents
          (cart.common/push-substep process-atom :success))
        (do
          (swap! ebucks-data-atom assoc :error (:msg response))
          (model/set-checkout-error app-atom)
          (if (= (:code response) "0002")
            (cart.common/push-substep process-atom :error-otp)
            (cart.common/push-substep process-atom :error)))))))

;; eBucks Step 02: Claim amount
(rum/defc ebucks-step-02-claim < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [ebucks-data (rum/cursor-in app-atom [:cart :ebucks-payment])
        amount-atom (rum/cursor-in ebucks-data [:amount])
        amount (rum/react amount-atom)
        accounts (rum/react (rum/cursor-in ebucks-data [:accounts]))
        max-balance-account
        (when accounts
          (apply max-key :balance accounts))
        available-balance-cents (or (:balance max-balance-account) 0)  ; Balance is in cents
        order (get-in (rum/react cart-atom) [:order])
        order-number (:number order)

        settle-without-otp
        (fn []
          (submit-payment app-atom process-atom ebucks-data order amount ""))

        handle-claim
        (fn [e]
          (.preventDefault e)
          (model/clear-checkout-error app-atom)
          (when (and (not (nil? amount)) (> amount 0) (<= (trf/tocurrency amount) available-balance-cents))
            (swap! ebucks-data assoc :selected-account (:number max-balance-account) :loading true :error nil)
            (if (:otp-exempt @ebucks-data)
              (settle-without-otp)
              (model/ebucks-send-otp app-atom
                {:order-uuid (:uuid order)
                 :payment-uuid (:payment-uuid @ebucks-data)
                 :session-token (get-in @ebucks-data [:session-token])
                 :amount (trf/tocurrency amount)}
                (fn [response]
                  (cond
                    (and (:success response) (false? (:otp_required response)))
                    (settle-without-otp)

                    (:success response)
                    (do
                      (swap! ebucks-data assoc :loading false)
                      (cart.common/push-substep process-atom :otp))

                    :else
                    (do
                      (swap! ebucks-data assoc :loading false)
                      (model/set-checkout-error app-atom)
                      (swap! ebucks-data assoc :error (or (:msg response) "Failed to send OTP. Please try again.")))))))))]

    (cart.common/checkout-layout process-atom false
      (list
        (when-let [error-msg (rum/react (rum/cursor-in ebucks-data [:error]))]
          (rum/with-key (feedback/feedback :error error-msg nil) "ebucks-error"))
        [:div {:key "ebucks-claim" :class "u-max-width-480"}
         [:div {:class "u-max-width-64 u-margin-bottom-16"}
          (icons/ebucks-icon)]
         [:h2 {:class "yc-heading yc-heading--default"} "How much of your eBucks would you like to spend?"]
         [:ul {:class "yc-list"}
          [:li (str "Your total available eBucks is R" (u/currency-format available-balance-cents))]
          [:li "You'll be sent an OTP when you claim your eBucks"]]
         (forms/input "Amount of eBucks to use in Rand:" "e.g. 500" amount-atom
           {:type :currency
            :name "conf[amount]"
            :id "amount"
            :class "js-text-input js-cta-input-trigger"
            :hint (let [ebucks-amt (* (or amount 0) 10)]
                    (str "This Rand amount is equal to eB" (u/number-format ebucks-amt)
                      " (i.e. R1 = eB10)"))})])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Use eBucks"
          {:on-click handle-claim
           :style :primary
           :class "checkout-cta__button"
           :disabled
           (or (nil? amount) (<= amount 0) (> (trf/tocurrency amount) available-balance-cents)
             (cmn/curact ebucks-data [:loading]))})
        nil
        order-number
        cart-atom))))

;; eBucks Step 03: OTP verification
(rum/defc ebucks-step-03-otp < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [ebucks-data-atom (rum/cursor-in app-atom [:cart :ebucks-payment])
        ebucks-data (rum/react ebucks-data-atom)
        otp (rum/cursor-in ebucks-data-atom [:otp])
        order (cmn/curact cart-atom [:order])
        amount (:amount ebucks-data)

        handle-otp-submit
        (fn [e]
          (.preventDefault e)
          (model/clear-checkout-error app-atom)
          (when (not-empty @otp)
            (submit-payment app-atom process-atom ebucks-data-atom order amount @otp)))

        handle-resend-otp
        (fn [e]
          (.preventDefault e)
          (model/clear-checkout-error app-atom)
          (swap! ebucks-data-atom assoc :loading true :otp-resend-feedback nil)
          (model/ebucks-send-otp app-atom
            {:order-uuid (:uuid order)
             :payment-uuid (:payment-uuid ebucks-data)
             :session-token (:session-token ebucks-data)
             :amount (trf/tocurrency amount)}
            (fn [response]
              (swap! ebucks-data-atom assoc :loading false)
              (if (:success response)
                (swap! ebucks-data-atom assoc :otp-resend-feedback {:type :success :message "OTP sent successfully"})
                (swap! ebucks-data-atom assoc :otp-resend-feedback {:type :error :message (or (:msg response) "Failed to send OTP. Please try again.")})))))]

    (cart.common/checkout-layout process-atom false
      (list
        (when-let [feedback-data (rum/react (rum/cursor-in ebucks-data-atom [:otp-resend-feedback]))]
          (rum/with-key (feedback/feedback (:type feedback-data) (:message feedback-data) nil) "otp-feedback"))
        [:div {:key "ebucks-otp" :class "u-max-width-480"}
         [:div {:class "u-max-width-64 u-margin-bottom-16"}
          (icons/ebucks-icon)]
         [:h2 {:class "yc-heading yc-heading--default"} "Verify your eBucks claim"]
         [:p {:class "yc-paragraph"}
          "Please enter your One Time PIN (OTP) sent to you by SMS to confirm and complete your payment."]
         (forms/input "eBucks OTP:" "e.g. 123456" otp
           {:type :password
            :name "conf[otp]"
            :id "otp"
            :class "js-text-input js-cta-input-trigger"
            :hint "6 characters, numbers only, e.g. 123456"
            :maxlength 6
            :small-width true})
         [:p {:class "yc-paragraph"}
          "If you don't receive your OTP within 5 minutes, try "
          [:a {:href "#" :class "text-link text-link--muted js-resend-otp"
               :on-click handle-resend-otp} "resend it"]]])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Pay with eBucks"
          {:on-click handle-otp-submit
           :style :primary
           :class "checkout-cta__button"
           :disabled (empty? (rum/react otp))})
        nil
        (:number order)
        cart-atom))))

;; eBucks Step 04a: OTP Error
(rum/defc ebucks-step-04a-error-otp < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [ebucks-data (rum/cursor-in app-atom [:cart :ebucks-payment])
        otp (rum/cursor-in ebucks-data [:otp])
        order (cmn/curact cart-atom [:order])
        order-number (:number order)
        amount (cmn/curact ebucks-data [:amount])
        otp-value (rum/react otp)

        ;; Resubmit straight away with the corrected OTP. (Previously this read
        ;; the cursor with rum/react inside the click handler, which only works
        ;; during render, and then only navigated back to the OTP step without
        ;; paying — the button did nothing.)
        handle-retry
        (fn [e]
          (.preventDefault e)
          (model/clear-checkout-error app-atom)
          (when (not-empty @otp)
            (cart.common/pop-substep process-atom)
            (submit-payment app-atom process-atom ebucks-data order amount @otp)))]

    (cart.common/checkout-layout process-atom false
      (list
        (rum/with-key
          (feedback/feedback :error
            "OTP incorrect, please try again"
            nil)
          "otp-error")
        [:div {:key "ebucks-retry" :class "u-max-width-480"}
         [:div {:class "u-max-width-64 u-margin-bottom-16"}
          (icons/ebucks-icon)]
         [:h2 {:class "yc-heading yc-heading--default"} "Verify your eBucks claim"]
         [:p {:class "yc-paragraph"}
          "Please enter your One Time PIN (OTP) sent to you by SMS to confirm and complete your payment."]
         (forms/input "eBucks OTP:" "e.g. 123456" otp
           {:type :password
            :name "conf[otp]"
            :id "otp"
            :class "js-text-input js-cta-input-trigger"
            :error "Incorrect OTP entered, check you haven't made a typo and try again. 6 characters, numbers only, e.g. 123456"
            :small-width true})
         [:p {:class "yc-paragraph yc-paragraph--flush"}
          "Otherwise you can choose a "
          [:a.text-link
           (urlstate/href-props app-atom "order" "ShowCart"
             {:orderid (:id order) :h (:h order) :step "payment/method-postcheckout" :method "eBucks"})
           "different payment method?"]]])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Retry eBucks"
          {:on-click handle-retry
           :style :primary
           :class "checkout-cta__button"
           :disabled (or (empty? otp-value) (cmn/curact ebucks-data [:loading]))})
        nil
        order-number
        cart-atom))))

;; eBucks Step 04b: General Error Response
(rum/defc ebucks-step-04b-error-response < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [order (cmn/curact cart-atom [:order])
        ebucks-data (rum/cursor-in app-atom [:cart :ebucks-payment])
        error-message (rum/react (rum/cursor-in ebucks-data [:error]))]

    (cart.common/checkout-layout process-atom false
      (list
        (rum/with-key
          (feedback/feedback :error
            "There was a problem with your eBucks payment"
            (str "The message we got back was: " (or error-message "Unknown error") "."))
          "ebucks-error")
        [:div {:key "ebucks-error-content" :class "u-max-width-480"}
         [:div {:class "u-max-width-64 u-margin-bottom-16"}
          (icons/ebucks-icon)]
         [:h2 {:class "yc-heading yc-heading--default"} "Please contact eBucks to help you"]
         [:p {:class "yc-paragraph"}
          "Please contact eBucks directly on "
          [:a {:href "tel:+27873203200" :class "text-link text-link--inherit js-prevent-default"} "087 320 3200"]
          " or manage your account by logging into eBucks at "
          [:a {:href "https://www.ebucks.com/web/eBucks/" :class "text-link text-link--inherit js-prevent-default"}
           "https://www.ebucks.com/web/eBucks/"]]
         [:p {:class "yc-paragraph"}
          "Otherwise you can choose a "
          [:a (urlstate/href-props app-atom "order" "ShowCart"
                {:orderid (:id order) :h (:h order) :step "payment/method-postcheckout" :method "eBucks"})
           "different payment method?"]]
         (buttons/button-web "Change payment method"
           {:on-click
            (fn [e]
              (.preventDefault e)
              (model/clear-checkout-error app-atom)
              (urlstate/redirect!
                (str "/order.htm?action=ShowCart"
                  "&step=payment%2Fmethod-postcheckout"
                  "&orderid=" (:id order)
                  "&h=" (:h order))))
            :style :secondary
            :icon :arrow-right
            :icon-placement :right
            :class "button--has-icon-right"})])

      (cart.common/checkout-cta-sidebar-simple
        nil
        nil
        (:number order)
        cart-atom))))

;; eBucks Step 05: Success
(rum/defc ebucks-step-05-success < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [order (get-in (rum/react cart-atom) [:order])
        order-number (:number order)
        balance (cmn/curact cart-atom [:totals :balance])
        has-balance? (and balance (pos? balance))]
    (cart.common/checkout-layout process-atom false
      (list
        (rum/with-key
          (feedback/feedback :success
            "eBucks payment successful!"
            "Your eBucks payment has been processed successfully.")
          "ebucks-success")
        [:h2 {:key "confirmation-heading" :class "yc-heading yc-heading--default"} "Order Confirmation"]
        [:p {:key "order-number" :class "yc-paragraph"}
         "Your order number is " [:b order-number]]
        (if has-balance?
          [:p {:key "balance-note" :class "yc-paragraph"}
           "Your eBucks payment has been applied. You still have an outstanding balance to pay."]
          [:p {:key "balance-note" :class "yc-paragraph"}
           "We've sent you a confirmation email with your order details."]))

      (cart.common/checkout-cta-sidebar-simple
        (if has-balance?
          (buttons/button-web "Continue"
            {:on-click
             (fn [e]
               (.preventDefault e)
               (urlstate/redirect!
                 (str "/order.htm?action=ShowCart"
                   "&step=payment%2Fmethod-postcheckout"
                   "&orderid=" (:id order)
                   "&h=" (:h order))))
             :style :primary
             :class "checkout-cta__button"})
          (buttons/button-web "Continue shopping"
            {:on-click #(urlstate/redirect app-atom nil)
             :style :cta
             :class "checkout-cta__button"}))
        nil
        order-number
        cart-atom
        {:total-key :balance}))))

;; Step dispatch functions

(rum/defc ebucks-page < rum/reactive cmn/cursors
  [app-atom process-atom cart-atom]
  (let [ebucks-state (cart.common/current-substep process-atom)]
    (case ebucks-state
      :login (ebucks-step-01-login app-atom process-atom cart-atom)
      :claim (ebucks-step-02-claim app-atom process-atom cart-atom)
      :otp (ebucks-step-03-otp app-atom process-atom cart-atom)
      :error-otp (ebucks-step-04a-error-otp app-atom process-atom cart-atom)
      :error (ebucks-step-04b-error-response app-atom process-atom cart-atom)
      :success (ebucks-step-05-success app-atom process-atom cart-atom)
      ;; Default to login step
      (ebucks-step-01-login app-atom process-atom cart-atom))))